import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import './styles/darkmode.css';
import Header from './components/Header';
import FooterNew from './components/FooterNew';
import ScrollToTop from './components/ScrollToTop';
// Sticky contact bar removed - using header buttons instead
import HomeModern from './pages/HomeModern';
import './styles/HomeModern.css';

// Only the homepage (plus the shell: Header/Footer/providers) is eagerly imported.
// Every other route is a lazy chunk, so a visitor landing on "/" downloads the
// homepage and nothing else — React mounts and replaces the static SEO fallback
// in index.html almost immediately instead of after the whole 46-page bundle.
const Inventory = lazy(() => import('./pages/Inventory'));
const VehicleDetail = lazy(() => import('./pages/VehicleDetail'));
// Vehicles page removed - using Inventory instead
// Golf cart & trailer pages removed per owner — site now sells/services cars only
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Financing = lazy(() => import('./pages/Financing'));
const Service = lazy(() => import('./pages/Service'));
const ServicesIndex = lazy(() => import('./pages/locations/services/ServicesIndex'));
// DieselRepair removed — truck content removed per owner
const EuropeanCarRepair = lazy(() => import('./pages/locations/services/EuropeanCarRepair'));
const BMWService = lazy(() => import('./pages/locations/services/european-car-repair/BMW'));
const AudiService = lazy(() => import('./pages/locations/services/european-car-repair/Audi'));
const JaguarService = lazy(() => import('./pages/locations/services/european-car-repair/Jaguar'));
const VolvoService = lazy(() => import('./pages/locations/services/european-car-repair/Volvo'));
const MercedesService = lazy(() => import('./pages/locations/services/european-car-repair/Mercedes'));
const PorscheService = lazy(() => import('./pages/locations/services/european-car-repair/Porsche'));
const VolkswagenService = lazy(() => import('./pages/locations/services/european-car-repair/Volkswagen'));
const VolkswagenInventory = lazy(() => import('./pages/inventory/VolkswagenInventory'));
const BMWInventory = lazy(() => import('./pages/inventory/BMWInventory'));
const AudiInventory = lazy(() => import('./pages/inventory/AudiInventory'));
const MiniCooperService = lazy(() => import('./pages/locations/services/european-car-repair/MiniCooper'));
const AlfaRomeoService = lazy(() => import('./pages/locations/services/european-car-repair/AlfaRomeo'));
const LandRoverService = lazy(() => import('./pages/locations/services/european-car-repair/LandRover'));
const ClutchRepair = lazy(() => import('./pages/locations/services/ClutchRepair'));
const CheckEngineLight = lazy(() => import('./pages/locations/services/CheckEngineLight'));
const WheelService = lazy(() => import('./pages/locations/services/WheelService'));
const TimingChainBeltReplacement = lazy(() => import('./pages/locations/services/TimingChainBeltReplacement'));
const OilChangeService = lazy(() => import('./pages/locations/services/OilChange'));
const DallasEuropeanAutoRepair = lazy(() => import('./pages/locations/DallasEuropeanAutoRepair'));
const DallasBMWRepair = lazy(() => import('./pages/locations/DallasBMWRepair'));
const DecaturTX = lazy(() => import('./pages/locations/vehicle-sales/DecaturTX'));
const WiseCounty = lazy(() => import('./pages/locations/vehicle-sales/WiseCounty'));
const BridgeportTX = lazy(() => import('./pages/locations/vehicle-sales/BridgeportTX'));
const WeatherfordTX = lazy(() => import('./pages/locations/vehicle-sales/WeatherfordTX'));
const DentonTX = lazy(() => import('./pages/locations/vehicle-sales/DentonTX'));
const TradeIn = lazy(() => import('./pages/TradeIn'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const PorschePSMFailure = lazy(() => import('./pages/blog/PorschePSMFailureFixed'));
const VWEPCLight = lazy(() => import('./pages/blog/VWEPCLightFixed'));
const BMWOilChangeJiffyLube = lazy(() => import('./pages/blog/BMWOilChangeJiffyLube'));
const BMWCommonProblems = lazy(() => import('./pages/blog/BMWCommonProblems'));
const PorscheCayenneSuspensionDallas = lazy(() => import('./pages/blog/PorscheCayenneSuspensionDallas'));
const PorscheCayenneLifespan = lazy(() => import('./pages/blog/PorscheCayenneLifespan'));
const LocalSEO = lazy(() => import('./pages/LocalSEO'));
const Admin = lazy(() => import('./pages/Admin'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const FinanceApplication = lazy(() => import('./pages/FinanceApplication'));
const CreditTerms = lazy(() => import('./pages/CreditTerms'));
const PreApproval = lazy(() => import('./pages/PreApproval'));
const NotFound = lazy(() => import('./pages/NotFound'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const ThankYou = lazy(() => import('./pages/ThankYou'));
const SubmissionSuccess = lazy(() => import('./pages/SubmissionSuccess'));
const CustomerPaymentPortal = lazy(() => import('./pages/CustomerPaymentPortal'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Warranty = lazy(() => import('./pages/Warranty'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const Disclaimer = lazy(() => import('./pages/Disclaimer'));
// Diesel truck pages removed per owner
const TransmissionRepair = lazy(() => import('./pages/locations/services/TransmissionRepair'));
const SuspensionRepair = lazy(() => import('./pages/locations/services/SuspensionRepair'));
const VehicleDiagnostics = lazy(() => import('./pages/locations/services/VehicleDiagnostics'));
const RadiatorRepair = lazy(() => import('./pages/locations/services/RadiatorRepair'));
const FactoryMaintenance = lazy(() => import('./pages/locations/services/FactoryMaintenance'));
const DrivelineDifferential = lazy(() => import('./pages/locations/services/DrivelineDifferential'));
const PerformanceUpgrades = lazy(() => import('./pages/locations/services/PerformanceUpgrades'));
const RangeRover = lazy(() => import('./pages/locations/services/european-car-repair/RangeRover'));
const PartsStore = lazy(() => import('./pages/PartsStore'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
import { CartProvider } from './contexts/CartContext';
import './App.css';
import './styles/slick-vars.css';

// Placeholder shown only while a lazy route chunk is in flight. The homepage never
// hits it (HomeModern is eager); it keeps the viewport from collapsing during a
// navigation to any other page.
function RouteFallback() {
  return <div style={{ minHeight: '60vh' }} aria-busy="true" />;
}

function App() {
  // Initialize Preline UI
  useEffect(() => {
    // Wait for document to be fully loaded
    const observer = new MutationObserver(() => {
      if (document.readyState === 'complete') {
        if (window.HSStaticMethods) {
          window.HSStaticMethods.autoInit();
        }
        observer.disconnect();
      }
    });

    observer.observe(document, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  return (
    <HelmetProvider>
      <CartProvider>
        <Router>
          <ScrollToTop />
          <div className="App min-h-screen bg-gray-50">
            <AnimatePresence mode="wait">
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                <Route path="/admin" element={<Admin />} />
                <Route path="/ADMIN" element={<Admin />} />
                <Route path="/Admin" element={<Admin />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route
                  path="/*"
                  element={
                    <>
                      <Header />
                      <Suspense fallback={<RouteFallback />}>
                        <Routes>
                        <Route path="/" element={<HomeModern />} />
                        <Route path="/vehicles" element={<Navigate to="/inventory" replace />} />
                        <Route path="/inventory" element={<Inventory />} />
                        <Route path="/inventory/volkswagen" element={<VolkswagenInventory />} />
                        <Route path="/inventory/bmw" element={<BMWInventory />} />
                        <Route path="/inventory/audi" element={<AudiInventory />} />
                        {/* Golf cart & trailer pages removed — redirect legacy URLs to inventory/services */}
                        <Route path="/golf-carts" element={<Navigate to="/inventory" replace />} />
                        <Route path="/golf-cart-services" element={<Navigate to="/services" replace />} />
                        <Route path="/golf-cart-repair" element={<Navigate to="/services" replace />} />
                        <Route path="/golf-cart/:id" element={<Navigate to="/inventory" replace />} />
                        <Route path="/trailer/:id" element={<Navigate to="/inventory" replace />} />
                        <Route path="/trailers" element={<Navigate to="/inventory" replace />} />
                        <Route path="/vehicle/:id" element={<VehicleDetail />} />
                        <Route path="/payment-success" element={<PaymentSuccess />} />
                        <Route path="/thank-you" element={<ThankYou />} />
                        <Route path="/submission-success" element={<SubmissionSuccess />} />
                        <Route path="/my-payments" element={<CustomerPaymentPortal />} />
                        <Route path="/payment-portal" element={<CustomerPaymentPortal />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/warranty" element={<Warranty />} />
                        <Route path="/appointment" element={<Service />} />
                        {/* Redirect old service URL to appointment */}
                        <Route path="/service" element={<Navigate to="/appointment" replace />} />
                        {/* === PRIMARY SERVICE PAGES (CANONICAL URLs - Option 1 Structure) === */}
                        <Route path="/services" element={<ServicesIndex />} />

                        {/* European Car Repair Hub & Brand Pages */}
                        <Route path="/services/european-car-repair" element={<EuropeanCarRepair />} />
                        <Route path="/services/european-car-repair/bmw" element={<BMWService />} />
                        <Route path="/services/european-car-repair/audi" element={<AudiService />} />
                        <Route path="/services/european-car-repair/mercedes" element={<MercedesService />} />
                        <Route path="/services/european-car-repair/porsche" element={<PorscheService />} />
                        <Route path="/services/european-car-repair/jaguar" element={<JaguarService />} />
                        <Route path="/services/european-car-repair/volvo" element={<VolvoService />} />
                        <Route path="/services/european-car-repair/volkswagen" element={<VolkswagenService />} />
                        <Route path="/services/european-car-repair/mini-cooper" element={<MiniCooperService />} />
                        <Route path="/services/european-car-repair/land-rover" element={<LandRoverService />} />
                        <Route path="/services/european-car-repair/alfa-romeo" element={<AlfaRomeoService />} />

                        {/* Diesel/truck pages removed — redirect to services */}
                        <Route path="/services/dallas-diesel-paradise-pros" element={<Navigate to="/services" replace />} />
                        <Route path="/services/dallas-diesel-paradise-pros/*" element={<Navigate to="/services" replace />} />
                        <Route path="/services/diesel/*" element={<Navigate to="/services" replace />} />

                        {/* General Service Pages */}
                        <Route path="/services/transmission-repair" element={<TransmissionRepair />} />
                        <Route path="/services/suspension-repair" element={<SuspensionRepair />} />
                        <Route path="/services/vehicle-diagnostics" element={<VehicleDiagnostics />} />
                        <Route path="/services/radiator-repair" element={<RadiatorRepair />} />
                        <Route path="/services/factory-maintenance" element={<FactoryMaintenance />} />
                        <Route path="/services/driveline-differential" element={<DrivelineDifferential />} />
                        <Route path="/services/performance-upgrades" element={<PerformanceUpgrades />} />

                        {/* Additional Service Pages */}
                        <Route path="/services/timing-chain-belt-replacement" element={<TimingChainBeltReplacement />} />
                        <Route path="/services/clutch-repair" element={<ClutchRepair />} />
                        <Route path="/services/diagnostic-check-engine-light" element={<CheckEngineLight />} />
                        <Route path="/services/wheel-and-tire" element={<WheelService />} />
                        <Route path="/services/oil-change" element={<OilChangeService />} />

                        {/* Legacy URL redirects for old service pages */}
                        <Route path="/services/differential-driveline-repair" element={<Navigate to="/services/driveline-differential" replace />} />
                        <Route path="/services/car-ac-repair" element={<Navigate to="/services" replace />} />

                        {/* View Inventory Redirect */}
                        <Route path="/view-inventory" element={<Navigate to="/inventory" replace />} />

                        {/* === LOCATION-SPECIFIC LANDING PAGES (UNIQUE CONTENT) === */}
                        <Route path="/dallas-european-auto-repair" element={<DallasEuropeanAutoRepair />} />
                        <Route path="/dallas-bmw-repair" element={<DallasBMWRepair />} />

                        {/* Vehicle Sales Location Pages */}
                        <Route path="/used-cars-decatur-tx" element={<DecaturTX />} />
                        <Route path="/used-cars-wise-county" element={<WiseCounty />} />
                        <Route path="/used-cars-bridgeport-tx" element={<BridgeportTX />} />
                        <Route path="/used-cars-weatherford-tx" element={<WeatherfordTX />} />
                        <Route path="/used-cars-denton-tx" element={<DentonTX />} />
                        {/* Old truck-specific page — redirect to inventory */}
                        <Route path="/used-trucks-fort-worth" element={<Navigate to="/inventory" replace />} />

                        {/* === REDIRECTS FROM OLD/DUPLICATE URLs === */}
                        {/* Redirect all /repair/* to /services/* */}
                        <Route path="/repair" element={<Navigate to="/services" replace />} />
                        <Route path="/repair/diesel" element={<Navigate to="/services" replace />} />
                        <Route path="/repair/european-vehicles" element={<Navigate to="/services/european-car-repair" replace />} />
                        <Route path="/repair/european-vehicles/bmw" element={<Navigate to="/services/european-car-repair/bmw" replace />} />
                        <Route path="/repair/european-vehicles/audi" element={<Navigate to="/services/european-car-repair/audi" replace />} />
                        <Route path="/repair/european-vehicles/mercedes" element={<Navigate to="/services/european-car-repair/mercedes" replace />} />
                        <Route path="/repair/european-vehicles/porsche" element={<Navigate to="/services/european-car-repair/porsche" replace />} />
                        <Route path="/repair/european-vehicles/jaguar" element={<Navigate to="/services/european-car-repair/jaguar" replace />} />
                        <Route path="/repair/european-vehicles/volvo" element={<Navigate to="/services/european-car-repair/volvo" replace />} />
                        <Route path="/repair/european-vehicles/volkswagen" element={<Navigate to="/services/european-car-repair/volkswagen" replace />} />
                        <Route path="/repair/european-vehicles/mini-cooper" element={<Navigate to="/services/european-car-repair/mini-cooper" replace />} />
                        <Route path="/repair/european-vehicles/land-rover" element={<Navigate to="/services/european-car-repair/land-rover" replace />} />
                        <Route path="/repair/european-vehicles/alfa-romeo" element={<Navigate to="/services/european-car-repair/alfa-romeo" replace />} />
                        <Route path="/repair/european-vehicles/range-rover" element={<Navigate to="/services/european-car-repair/land-rover" replace />} />

                        {/* Redirect duplicate location pages */}
                        <Route path="/dallas-porsche-repair" element={<Navigate to="/services/european-car-repair/porsche" replace />} />
                        <Route path="/dallas-mercedes-repair" element={<Navigate to="/services/european-car-repair/mercedes" replace />} />
                        <Route path="/dallas-audi-repair" element={<Navigate to="/services/european-car-repair/audi" replace />} />

                        <Route path="/disclaimer" element={<Disclaimer />} />
                        <Route path="/privacy" element={<Privacy />} />
                        <Route path="/privacy-policy" element={<Privacy />} />
                        <Route path="/refund-policy" element={<RefundPolicy />} />
                        <Route path="/cookies" element={<Navigate to="/cookie-policy" replace />} />
                        <Route path="/cookie-policy" element={<CookiePolicy />} />
                        <Route path="/terms" element={<TermsAndConditions />} />
                        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                        {/* Financing page temporarily disabled - redirects to finance application */}
                        <Route path="/financing" element={<Navigate to="/finance-application" replace />} />
                        <Route path="/trade-in" element={<TradeIn />} />
                        <Route path="/blog" element={<Blog />} />
                        <Route path="/blog/porsche-psm-failure-guide" element={<PorschePSMFailure />} />
                        <Route path="/blog/fix-vw-epc-light" element={<VWEPCLight />} />
                        <Route path="/blog/can-jiffy-lube-change-bmw-oil" element={<BMWOilChangeJiffyLube />} />
                        <Route path="/blog/common-bmw-problems" element={<BMWCommonProblems />} />
                        <Route path="/blog/porsche-cayenne-suspension-dallas" element={<PorscheCayenneSuspensionDallas />} />
                        <Route path="/blog/porsche-cayenne-lifespan" element={<PorscheCayenneLifespan />} />
                        <Route path="/blog/common-car-ac-problems" element={<Navigate to="/services" replace />} />
                        <Route path="/blog/:slug" element={<BlogPost />} />
                        <Route path="/dealers/dallas" element={<LocalSEO />} />
                        <Route path="/ai-tools" element={<Navigate to="/services" replace />} />
                        <Route path="/finance-application" element={<FinanceApplication />} />
                        <Route path="/credit-terms" element={<CreditTerms />} />
                        <Route path="/pre-approval" element={<PreApproval />} />
                        <Route path="/parts" element={<PartsStore />} />
                        <Route path="/parts/:slug" element={<ProductDetail />} />
                        <Route path="/cart" element={<Cart />} />
                        <Route path="/checkout" element={<Checkout />} />
                        <Route path="*" element={<NotFound />} />
                        </Routes>
                      </Suspense>
                      <FooterNew />
                      {/* Sticky bar removed - contact buttons are in the header */}
                      {/* ChatBot temporarily removed pending a valid OpenAI API key */}
                    </>
                  }
                />
                </Routes>
              </Suspense>
            </AnimatePresence>
          </div>
        </Router>
      </CartProvider>
    </HelmetProvider>
  );
}

export default App;